// FieldIQ — People tab — roster, network, teams

import { useState, useEffect, useCallback } from "react";
import {
  MessageSquare,
  Users,
  LogOut,
  Loader2,
  Send,
  Mail,
  Shield,
  ChevronRight,
  ChevronLeft,
  Plus,
  Upload,
  Search,
  DoorOpen,
  PhoneCall,
  MessageCircle,
  FileText,
  Trash2,
  Filter,
  Sparkles,
  Heart,
  Smartphone,
  Phone,
  X,
  UserPlus,
  Edit3,
  WifiOff,
} from "lucide-react";
import { Avatar } from "../components/ui";
import { FIELDIQ_API_URL, authFetch, getToken } from "../lib/api";
import { streakBadge } from "../lib/helpers";
import {
  loadContactActivity,
  loadNetwork,
  loadRoster,
  logContactActivity,
  saveNetwork,
  saveRoster,
  timeAgo,
} from "../lib/roster";
import { ThreadDetail, ThreadListItem } from "./ThreadsTab";

const PEOPLE_GRADIENTS = [
  "from-cyan-400 to-blue-500",
  "from-emerald-400 to-teal-500",
  "from-amber-400 to-orange-500",
  "from-rose-400 to-pink-500",
  "from-violet-400 to-purple-500",
  "from-sky-400 to-indigo-500",
];

const VOTER_STATUS_TAGS = {
  New: { label: "New", color: "purple" },
  Interested: { label: "Supporter", color: "emerald" },
  Supportive: { label: "Supportive", color: "cyan" },
  "Follow up": { label: "Callback", color: "amber" },
  "Not home": { label: "Not home", color: "slate" },
  Enrolled: { label: "Enrolled", color: "violet" },
};

const ACTIVITY_ICONS = {
  email: { icon: Mail, color: "text-blue-400", label: "Emailed" },
  sms: { icon: MessageCircle, color: "text-cyan-400", label: "Texted" },
  call: { icon: PhoneCall, color: "text-emerald-400", label: "Called" },
  visit: { icon: DoorOpen, color: "text-amber-400", label: "Visited" },
  note: { icon: FileText, color: "text-slate-400", label: "Note" },
};

const PeopleTagPill = ({ label, color }) => {
  const colorMap = {
    cyan: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    purple: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    slate: "bg-slate-600/30 text-slate-300 border-slate-500/30",
  };
  return (
    <span
      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${colorMap[color] ?? colorMap.slate}`}
    >
      {label}
    </span>
  );
};

const getPersonInitials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

// ═══════════════════════════════════════════════════════════════════
// MY NETWORK — relational organizing components
// ═══════════════════════════════════════════════════════════════════

const FilterChip = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-shrink-0 px-3 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap transition-colors ${
      active
        ? "bg-cyan-500/20 border-cyan-500/40 text-cyan-300"
        : "bg-slate-800/40 border-slate-700/50 text-slate-300"
    }`}
  >
    {label}
  </button>
);

const NetworkStat = ({ value, label, highlight }) => (
  <div className="flex flex-col">
    <div
      className={`text-2xl font-bold ${highlight ? "text-cyan-300" : "text-white"}`}
    >
      {value}
    </div>
    <div className="text-slate-400 text-[10px] mt-0.5">{label}</div>
  </div>
);

const BreakdownBar = ({ label, value, pct, color }) => {
  const colorMap = {
    slate: "bg-slate-500",
    cyan: "bg-gradient-to-r from-cyan-500 to-blue-500",
    amber: "bg-amber-500",
    rose: "bg-rose-500",
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-slate-300 text-xs">{label}</span>
        <span className="text-white text-xs font-semibold">{value}</span>
      </div>
      <div className="h-2 bg-slate-800/60 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorMap[color]}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const ActionCard = ({ title, subtitle, cta, primary }) => (
  <div
    className={`rounded-xl p-3.5 border flex items-center justify-between gap-3 ${
      primary
        ? "bg-gradient-to-br from-cyan-500/15 to-blue-600/5 border-cyan-500/30"
        : "bg-slate-800/40 border-slate-800/50"
    }`}
  >
    <div className="flex-1 min-w-0">
      <div className="text-white text-sm font-semibold">{title}</div>
      <div className="text-slate-400 text-xs mt-0.5">{subtitle}</div>
    </div>
    <button
      className={`flex-shrink-0 px-3.5 py-1.5 rounded-lg text-xs font-bold ${
        primary
          ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md shadow-blue-900/30"
          : "bg-slate-700/60 text-slate-200"
      }`}
    >
      {cta}
    </button>
  </div>
);

const NetworkPreviewRow = ({ icon, text }) => (
  <div className="bg-slate-800/30 rounded-lg px-3 py-2.5 flex items-center gap-3 border border-slate-800/40">
    <span className="text-base">{icon}</span>
    <span className="text-slate-300 text-xs">{text}</span>
  </div>
);

const NetworkCard = ({ contact, onDelete }) => {
  const hasPhone = contact.phone && contact.phone !== "—";
  const log = (type) => logContactActivity(contact.id, type);
  return (
    <div className="bg-slate-800/40 rounded-xl p-3.5 border border-slate-800/50">
      <div className="flex items-start gap-3">
        <div
          className={`w-11 h-11 rounded-full bg-gradient-to-br ${contact.gradient} flex items-center justify-center flex-shrink-0`}
        >
          <span className="text-white font-bold text-sm">
            {contact.initials}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="text-white font-semibold text-sm">
              {contact.name}
            </div>
            <div className="text-slate-500 text-[11px]">
              · {contact.relation}
            </div>
          </div>
          <div className="text-slate-400 text-xs mt-0.5">{contact.meta}</div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {contact.tags.map((tag, i) => (
              <PeopleTagPill key={i} label={tag.label} color={tag.color} />
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => {
            if (hasPhone) {
              window.open(`sms:${contact.phone}`);
              log("sms");
            }
          }}
          className="flex-1 py-2 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-xs font-semibold flex items-center justify-center gap-1.5"
        >
          <Send size={12} /> Send Text
        </button>
        <button
          onClick={() => {
            if (hasPhone) {
              window.open(`tel:${contact.phone}`);
              log("call");
            }
          }}
          className="px-3.5 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50 text-slate-300 flex items-center justify-center"
        >
          <Phone size={13} />
        </button>
        {onDelete && (
          <button
            onClick={() => onDelete(contact.id)}
            className="px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50 text-rose-400 flex items-center justify-center"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

const NetworkInsightsView = ({ onBack }) => (
  <div className="space-y-5">
    <button
      onClick={onBack}
      className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300"
    >
      <ChevronLeft size={16} /> My Network
    </button>

    {/* Big number hero */}
    <div className="rounded-2xl p-5 bg-gradient-to-br from-cyan-500/20 via-blue-600/10 to-transparent border border-cyan-500/30 text-center">
      <div className="text-cyan-300 text-[10px] font-bold tracking-widest mb-2">
        PEOPLE YOU CAN INFLUENCE
      </div>
      <div className="text-white text-5xl font-bold">34</div>
      <div className="text-slate-300 text-sm mt-1">
        voters in your network in LD4
      </div>
    </div>

    {/* Breakdown */}
    <div>
      <p className="text-[10px] font-bold text-slate-300 tracking-widest mb-3">
        BREAKDOWN
      </p>
      <div className="space-y-3">
        <BreakdownBar
          label="Total contacts"
          value="247"
          pct={100}
          color="slate"
        />
        <BreakdownBar
          label="Registered voters"
          value="89"
          pct={36}
          color="cyan"
        />
        <BreakdownBar label="In LD4" value="34" pct={14} color="cyan" />
        <BreakdownBar label="Skipped 2024" value="12" pct={5} color="amber" />
        <BreakdownBar label="Low propensity" value="7" pct={3} color="rose" />
      </div>
    </div>

    {/* The math */}
    <div className="bg-slate-800/40 border border-slate-800/50 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-cyan-400" />
        <span className="text-slate-300 text-[10px] font-bold tracking-widest">
          THE MATH
        </span>
      </div>
      <p className="text-white text-sm leading-relaxed mb-3">
        Texts from friends turn out voters at{" "}
        <span className="text-cyan-300 font-bold">8×</span> the rate of texts
        from strangers.
      </p>
      <div className="bg-slate-900/60 rounded-xl p-3 border border-slate-800/50">
        <div className="text-slate-400 text-xs">
          If you reach all 12 LD4 contacts who skipped 2024:
        </div>
        <div className="text-white text-lg font-bold mt-1">
          ~10 likely voter activations
        </div>
        <div className="text-slate-500 text-[10px] mt-1">
          Based on relational organizing data
        </div>
      </div>
    </div>

    {/* Suggested actions */}
    <div>
      <p className="text-[10px] font-bold text-slate-300 tracking-widest mb-3">
        SUGGESTED ACTIONS
      </p>
      <div className="space-y-2">
        <ActionCard
          title="Reach the 12"
          subtitle="Text your LD4 contacts who skipped 2024"
          cta="Start"
          primary
        />
        <ActionCard
          title="Family activation"
          subtitle="6 family members are registered voters"
          cta="View"
        />
        <ActionCard
          title="Same precinct, same fight"
          subtitle="3 contacts live in PC-04-12"
          cta="View"
        />
      </div>
    </div>
  </div>
);

const MyNetworkView = ({ contacts, onImportAll, onImportSingle, onDelete }) => {
  const [showInsights, setShowInsights] = useState(false);
  const [search, setSearch] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addRelation, setAddRelation] = useState("");
  const [saving, setSaving] = useState(false);

  if (showInsights) {
    return <NetworkInsightsView onBack={() => setShowInsights(false)} />;
  }

  const submitSingle = async () => {
    if (!addName.trim()) return;
    setSaving(true);
    await onImportSingle({
      name: addName.trim(),
      phone: addPhone.trim() || null,
      relation: addRelation.trim() || "Contact",
      tags: [],
    });
    setAddName("");
    setAddPhone("");
    setAddRelation("");
    setShowAddForm(false);
    setSaving(false);
  };

  if (contacts.length === 0) {
    return (
      <div className="space-y-5">
        {/* Hero */}
        <div className="rounded-2xl p-5 bg-gradient-to-br from-cyan-500/15 via-blue-600/10 to-transparent border border-cyan-500/30 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center mb-3">
              <Heart size={22} className="text-white" fill="white" />
            </div>
            <div className="text-white text-lg font-bold leading-tight mb-2">
              Activate your circle
            </div>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              A text from a friend turns out 8× more voters than one from a
              stranger. Import your contacts to find the people you already know
              who can make a difference.
            </p>
            <div className="flex gap-2">
              <button
                onClick={onImportAll}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-blue-900/30"
              >
                <Smartphone size={16} />
                Import All
              </button>
              <button
                onClick={() => setShowAddForm(true)}
                className="px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-300 font-semibold flex items-center justify-center gap-2"
              >
                <UserPlus size={16} />
                Add One
              </button>
            </div>
          </div>
        </div>

        {showAddForm && (
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
            <p className="text-white text-sm font-semibold">Add a contact</p>
            <input
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="Name *"
              className="w-full px-3 py-2.5 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
            />
            <input
              value={addPhone}
              onChange={(e) => setAddPhone(e.target.value)}
              placeholder="Phone (optional)"
              type="tel"
              className="w-full px-3 py-2.5 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
            />
            <input
              value={addRelation}
              onChange={(e) => setAddRelation(e.target.value)}
              placeholder="Relation (e.g. Neighbor, Friend)"
              className="w-full px-3 py-2.5 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
            />
            <div className="flex gap-2">
              <button
                onClick={submitSingle}
                disabled={!addName.trim() || saving}
                className="flex-1 py-2.5 rounded-lg bg-cyan-500 text-white text-sm font-semibold disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2.5 rounded-lg bg-slate-700 text-slate-300 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Privacy note */}
        <div className="bg-slate-800/40 rounded-xl p-3.5 border border-slate-800/50 flex gap-3">
          <Shield size={18} className="text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-white text-sm font-semibold mb-1">
              Your contacts stay yours
            </div>
            <p className="text-slate-400 text-xs leading-relaxed">
              We only store contacts you explicitly import. You can remove any
              contact at any time.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const filtered = contacts.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.relation || "").toLowerCase().includes(search.toLowerCase()),
  );

  const enriched = filtered.map((c, i) => ({
    ...c,
    initials: getPersonInitials(c.name),
    gradient: PEOPLE_GRADIENTS[i % PEOPLE_GRADIENTS.length],
    tags: Array.isArray(c.tags) ? c.tags : [],
  }));

  return (
    <div className="space-y-4">
      {/* Stats banner */}
      <div className="rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-600/5 border border-cyan-500/20 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-cyan-400" />
            <span className="text-cyan-300 text-[10px] font-bold tracking-widest">
              YOUR NETWORK
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowInsights(true)}
              className="text-cyan-400 text-xs font-medium"
            >
              Insights →
            </button>
            <button
              onClick={() => setShowAddForm((v) => !v)}
              className="text-cyan-400 text-xs font-medium flex items-center gap-1"
            >
              <UserPlus size={12} /> Add
            </button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <NetworkStat value={contacts.length} label="Contacts" />
          <NetworkStat value="—" label="In LD4" highlight />
          <NetworkStat value="—" label="Need nudge" highlight />
        </div>
      </div>

      {showAddForm && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
          <p className="text-white text-sm font-semibold">Add a contact</p>
          <input
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            placeholder="Name *"
            className="w-full px-3 py-2.5 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
          />
          <input
            value={addPhone}
            onChange={(e) => setAddPhone(e.target.value)}
            placeholder="Phone (optional)"
            type="tel"
            className="w-full px-3 py-2.5 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
          />
          <input
            value={addRelation}
            onChange={(e) => setAddRelation(e.target.value)}
            placeholder="Relation (e.g. Neighbor, Friend)"
            className="w-full px-3 py-2.5 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
          />
          <div className="flex gap-2">
            <button
              onClick={submitSingle}
              disabled={!addName.trim() || saving}
              className="flex-1 py-2.5 rounded-lg bg-cyan-500 text-white text-sm font-semibold disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2.5 rounded-lg bg-slate-700 text-slate-300 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your network..."
            className="w-full pl-8 pr-3 py-2.5 bg-slate-800/40 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
          />
        </div>
        <button
          onClick={onImportAll}
          className="px-3 py-2.5 rounded-lg bg-slate-800/40 border border-slate-700 flex items-center gap-1.5 text-slate-300 text-xs"
          title="Import all device contacts"
        >
          <Smartphone size={14} />
        </button>
      </div>

      {/* Contact list */}
      <div className="space-y-2.5">
        {enriched.length === 0 ? (
          <p className="text-center text-slate-500 text-sm py-8">
            No contacts match your search.
          </p>
        ) : (
          enriched.map((c) => (
            <NetworkCard key={c.id} contact={c} onDelete={onDelete} />
          ))
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CONTACT DETAIL VIEW — activity log + email/SMS actions
// ═══════════════════════════════════════════════════════════════════

const ContactDetailView = ({ contact, onBack, gradientClass }) => {
  const [activity, setActivity] = useState(() =>
    loadContactActivity(contact.id),
  );
  const [noteInput, setNoteInput] = useState("");
  const [showNote, setShowNote] = useState(false);

  const log = (type, note = "") => {
    setActivity(logContactActivity(contact.id, type, note));
  };

  const hasEmail = contact.email && contact.email !== "—";
  const hasPhone = contact.phone && contact.phone !== "—";

  const tag = VOTER_STATUS_TAGS[contact.status] ?? null;
  const subtitle = contact.title
    ? `${contact.title}${contact.precinct ? ` · ${contact.precinct}` : ""}`
    : null;

  return (
    <div className="space-y-5">
      {/* Back */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300"
      >
        <ChevronLeft size={16} /> People
      </button>

      {/* Header card */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5">
        <div className="flex items-center gap-4">
          <div
            className={`w-16 h-16 rounded-full bg-gradient-to-br ${gradientClass} flex items-center justify-center flex-shrink-0`}
          >
            <span className="text-white font-bold text-lg">
              {getPersonInitials(contact.name)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-bold text-lg leading-tight">
              {contact.name}
            </div>
            {subtitle && (
              <div className="text-slate-400 text-sm mt-0.5">{subtitle}</div>
            )}
            {tag && (
              <div className="mt-1.5">
                <PeopleTagPill label={tag.label} color={tag.color} />
              </div>
            )}
            {contact.badges && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {contact.badges.map((b, i) => (
                  <PeopleTagPill key={i} label={b.label} color={b.color} />
                ))}
              </div>
            )}
            {contact.streak_days >= 1 &&
              (() => {
                const { emojis, label } = streakBadge(contact.streak_days);
                return (
                  <div className="flex items-center gap-1.5 mt-2">
                    <span className="text-base leading-none">{emojis}</span>
                    <span className="text-xs text-slate-400">
                      {label} streak
                    </span>
                  </div>
                );
              })()}
          </div>
        </div>

        {(hasEmail || hasPhone) && (
          <div className="mt-4 pt-4 border-t border-slate-700 space-y-1.5">
            {hasEmail && (
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Mail size={13} className="text-slate-500 flex-shrink-0" />
                <span className="truncate">{contact.email}</span>
              </div>
            )}
            {hasPhone && (
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <PhoneCall size={13} className="text-slate-500 flex-shrink-0" />
                <span>{contact.phone}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action buttons */}
      {(hasEmail || hasPhone) && (
        <div className="flex gap-3">
          {hasEmail && (
            <button
              onClick={() => {
                window.open(`mailto:${contact.email}`);
                log("email");
              }}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600/20 border border-blue-500/30 rounded-xl text-blue-300 text-sm font-semibold hover:bg-blue-600/30 transition-colors"
            >
              <Mail size={15} /> Email
            </button>
          )}
          {hasPhone && (
            <button
              onClick={() => {
                window.open(`sms:${contact.phone}`);
                log("sms");
              }}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-cyan-600/20 border border-cyan-500/30 rounded-xl text-cyan-300 text-sm font-semibold hover:bg-cyan-600/30 transition-colors"
            >
              <MessageCircle size={15} /> SMS
            </button>
          )}
        </div>
      )}

      {/* Log activity */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
          Log Activity
        </p>
        <div className="flex gap-2 flex-wrap">
          {[
            { type: "call", label: "Called", icon: PhoneCall },
            { type: "visit", label: "Visited", icon: DoorOpen },
          ].map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              onClick={() => log(type)}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-800/60 border border-slate-700 rounded-lg text-slate-300 text-xs font-semibold hover:border-slate-500 transition-colors"
            >
              <Icon size={12} /> {label}
            </button>
          ))}
          <button
            onClick={() => setShowNote((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-800/60 border border-slate-700 rounded-lg text-slate-300 text-xs font-semibold hover:border-slate-500 transition-colors"
          >
            <FileText size={12} /> Note
          </button>
        </div>
        {showNote && (
          <div className="mt-2 flex gap-2">
            <input
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              placeholder="Add a note..."
              className="flex-1 px-3 py-2 bg-slate-800/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === "Enter" && noteInput.trim()) {
                  log("note", noteInput.trim());
                  setNoteInput("");
                  setShowNote(false);
                }
              }}
            />
            <button
              onClick={() => {
                if (noteInput.trim()) {
                  log("note", noteInput.trim());
                  setNoteInput("");
                  setShowNote(false);
                }
              }}
              className="px-3 py-2 bg-cyan-500 rounded-lg text-white text-xs font-semibold"
            >
              Save
            </button>
          </div>
        )}
      </div>

      {/* Activity timeline */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
          {activity.length === 0
            ? "No activity yet"
            : `Activity (${activity.length})`}
        </p>
        {activity.length === 0 ? (
          <p className="text-slate-600 text-sm text-center py-6">
            Log your first interaction above.
          </p>
        ) : (
          <div className="space-y-2">
            {activity.map((a) => {
              const meta = ACTIVITY_ICONS[a.type] ?? ACTIVITY_ICONS.note;
              const Icon = meta.icon;
              return (
                <div
                  key={a.id}
                  className="flex items-start gap-3 bg-slate-800/30 rounded-xl px-3.5 py-3 border border-slate-700/50"
                >
                  <Icon
                    size={14}
                    className={`mt-0.5 flex-shrink-0 ${meta.color}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium leading-tight">
                      {meta.label}
                    </div>
                    {a.note && (
                      <div className="text-xs text-slate-400 mt-0.5 leading-snug">
                        {a.note}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 flex-shrink-0 mt-0.5">
                    {timeAgo(a.ts)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TEAM DETAIL VIEW
// ═══════════════════════════════════════════════════════════════════

const TeamDetailView = ({
  team,
  userId,
  facilityUsers,
  onBack,
  onRefresh,
  user,
  facility,
}) => {
  const isOwner = team.owner_user_id === userId;
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(team.name);
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteSearch, setInviteSearch] = useState("");
  const [inviting, setInviting] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const [creatingThread, setCreatingThread] = useState(false);
  const [threadError, setThreadError] = useState(null);

  const fid = facility?.facility_id || "";

  const fetchTeamThreads = useCallback(async () => {
    if (!fid) return;
    setThreadsLoading(true);
    try {
      const r = await fetch(
        `${FIELDIQ_API_URL}/threads?ld_id=${encodeURIComponent(fid)}&status=active`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (!r.ok) return;
      const all = await r.json();
      setThreads(
        (Array.isArray(all) ? all : []).filter(
          (t) =>
            t.thread_type === "team" &&
            String(t.associated_entity_id) === String(team.id),
        ),
      );
    } finally {
      setThreadsLoading(false);
    }
  }, [fid, team.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTeamThreads();
  }, [fetchTeamThreads]);

  const members = Array.isArray(team.members) ? team.members : [];
  const activeMembers = members.filter((m) => m.status === "active");
  const pendingMembers = members.filter((m) => m.status === "invited");
  const existingIds = new Set(members.map((m) => m.user_id));
  const invitableUsers = facilityUsers.filter(
    (u) => !existingIds.has(u.user_id),
  );
  const filteredInvitable = inviteSearch
    ? invitableUsers.filter((u) =>
        (u.full_name || "").toLowerCase().includes(inviteSearch.toLowerCase()),
      )
    : invitableUsers;

  const handleRename = async () => {
    if (!nameInput.trim() || saving) return;
    setSaving(true);
    await authFetch(`/fieldiq/teams/${team.id}`, {
      method: "PATCH",
      body: JSON.stringify({ name: nameInput.trim() }),
    });
    setSaving(false);
    setEditingName(false);
    onRefresh();
  };

  const handleInvite = async (inviteeUserId) => {
    setInviting(inviteeUserId);
    await authFetch(`/fieldiq/teams/${team.id}/invite`, {
      method: "POST",
      body: JSON.stringify({ user_id: inviteeUserId }),
    });
    setInviting(null);
    setShowInvite(false);
    setInviteSearch("");
    onRefresh();
  };

  const handleRemove = async (memberUserId) => {
    setRemoving(memberUserId);
    await authFetch(`/fieldiq/teams/${team.id}/members/${memberUserId}`, {
      method: "DELETE",
    });
    setRemoving(null);
    onRefresh();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${team.name}"? This cannot be undone.`))
      return;
    await authFetch(`/fieldiq/teams/${team.id}`, { method: "DELETE" });
    onBack();
    onRefresh();
  };

  const handleMessageTeam = async () => {
    if (creatingThread) return;
    if (threads.length > 0) {
      setActiveThread(threads[0]);
      return;
    }
    setCreatingThread(true);
    setThreadError(null);
    try {
      const memberIds = activeMembers
        .map((m) => m.user_id)
        .filter((id) => id !== userId);
      const r = await fetch(`${FIELDIQ_API_URL}/threads`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ld_id: fid,
          title: team.name,
          thread_type: "team",
          associated_entity_id: team.id,
          member_ids: memberIds,
        }),
      });
      if (r.ok) {
        const newThread = await r.json();
        setActiveThread(newThread);
        fetchTeamThreads();
      } else {
        const body = await r.json().catch(() => ({}));
        setThreadError(body.detail || `Failed to create thread (${r.status})`);
      }
    } catch {
      setThreadError("Network error — check your connection and try again.");
    } finally {
      setCreatingThread(false);
    }
  };

  if (activeThread) {
    return (
      <ThreadDetail
        thread={activeThread}
        user={user}
        onBack={() => {
          setActiveThread(null);
          fetchTeamThreads();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300"
      >
        <ChevronLeft size={16} /> Teams
      </button>

      {/* Header */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4">
        {editingName ? (
          <div className="flex items-center gap-2">
            <input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              autoFocus
              className="flex-1 px-3 py-2 bg-slate-900/60 border border-slate-600 rounded-lg text-sm text-white focus:border-cyan-500 focus:outline-none"
            />
            <button
              onClick={handleRename}
              disabled={saving}
              className="px-3 py-2 bg-cyan-600 rounded-lg text-white text-xs font-semibold disabled:opacity-40"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : "Save"}
            </button>
            <button
              onClick={() => {
                setEditingName(false);
                setNameInput(team.name);
              }}
              className="px-3 py-2 bg-slate-700 rounded-lg text-slate-300 text-xs"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white">{team.name}</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {activeMembers.length} member
                {activeMembers.length !== 1 ? "s" : ""}
              </p>
            </div>
            {isOwner && (
              <button
                onClick={() => setEditingName(true)}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                <Edit3 size={15} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pending invites */}
      {pendingMembers.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
            Pending Invites
          </p>
          {pendingMembers.map((m) => (
            <div
              key={m.user_id}
              className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-3.5 flex items-center gap-3"
            >
              <Avatar
                name={m.full_name || ""}
                photoUrl={m.photo_url || null}
                size={9}
                colorClass="from-slate-500 to-slate-600"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-300 font-medium">
                  {m.full_name}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">Invite pending</p>
              </div>
              {isOwner && (
                <button
                  onClick={() => handleRemove(m.user_id)}
                  disabled={removing === m.user_id}
                  title="Withdraw invite"
                  className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"
                >
                  {removing === m.user_id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <X size={14} />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Active members */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
          Members
        </p>
        {activeMembers.map((m) => {
          const { emojis } = streakBadge(m.streak_days || 1);
          const isSelf = m.user_id === userId;
          const memberIsOwner = m.user_id === team.owner_user_id;
          return (
            <div
              key={m.user_id}
              className="bg-slate-800/40 border border-slate-700 rounded-xl p-3.5 flex items-center gap-3"
            >
              <Avatar
                name={m.full_name || ""}
                photoUrl={m.photo_url || null}
                size={10}
                colorClass="from-cyan-500 to-blue-600"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold text-white">
                    {m.full_name}
                  </span>
                  <span className="text-sm leading-none">{emojis}</span>
                  {memberIsOwner && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                      Owner
                    </span>
                  )}
                </div>
                {m.title && (
                  <p className="text-xs text-slate-400 mt-0.5">{m.title}</p>
                )}
              </div>
              {isOwner && !memberIsOwner && (
                <button
                  onClick={() => handleRemove(m.user_id)}
                  disabled={removing === m.user_id}
                  title="Remove from team"
                  className="p-1.5 text-slate-500 hover:text-red-400 transition-colors flex-shrink-0"
                >
                  {removing === m.user_id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <X size={14} />
                  )}
                </button>
              )}
              {!isOwner && isSelf && (
                <button
                  onClick={() => handleRemove(m.user_id)}
                  disabled={removing === m.user_id}
                  title="Leave team"
                  className="p-1.5 text-slate-500 hover:text-red-400 transition-colors flex-shrink-0"
                >
                  {removing === m.user_id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <LogOut size={14} />
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Invite picker */}
      {isOwner && (
        <div>
          {showInvite ? (
            <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">
                  Invite a member
                </p>
                <button
                  onClick={() => {
                    setShowInvite(false);
                    setInviteSearch("");
                  }}
                  className="text-slate-400 hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>
              <input
                value={inviteSearch}
                onChange={(e) => setInviteSearch(e.target.value)}
                placeholder="Search by name…"
                autoFocus
                className="w-full px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
              />
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {filteredInvitable.length === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-3">
                    No users to invite.
                  </p>
                ) : (
                  filteredInvitable.map((u) => (
                    <div
                      key={u.user_id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-700/40"
                    >
                      <Avatar
                        name={u.full_name || ""}
                        photoUrl={u.photo_url || null}
                        size={8}
                        colorClass="from-slate-500 to-slate-600"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white">{u.full_name}</p>
                        {u.title && (
                          <p className="text-xs text-slate-400">{u.title}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleInvite(u.user_id)}
                        disabled={inviting === u.user_id}
                        className="px-3 py-1.5 bg-cyan-600/80 text-white text-xs font-semibold rounded-lg hover:bg-cyan-500 disabled:opacity-40 flex items-center gap-1"
                      >
                        {inviting === u.user_id && (
                          <Loader2 size={11} className="animate-spin" />
                        )}
                        Invite
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowInvite(true)}
              className="w-full py-2.5 border border-dashed border-slate-600 rounded-xl text-slate-400 text-sm flex items-center justify-center gap-2 hover:border-cyan-600/50 hover:text-cyan-400 transition-colors"
            >
              <UserPlus size={15} />
              Invite Member
            </button>
          )}
        </div>
      )}

      {/* Team Threads */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
            Team Messages
          </p>
          {threadsLoading && (
            <Loader2 size={12} className="animate-spin text-slate-500" />
          )}
        </div>
        {threads.length === 0 && !threadsLoading ? (
          <p className="text-xs text-slate-500 text-center py-2">
            No threads yet.
          </p>
        ) : (
          threads.map((t) => (
            <ThreadListItem key={t.id} thread={t} onOpen={setActiveThread} />
          ))
        )}
        {threadError && (
          <p className="text-xs text-rose-400 text-center px-2">
            {threadError}
          </p>
        )}
        <button
          onClick={handleMessageTeam}
          disabled={creatingThread}
          className="w-full py-2.5 border border-dashed border-slate-600 rounded-xl text-slate-400 text-sm flex items-center justify-center gap-2 hover:border-cyan-600/50 hover:text-cyan-400 transition-colors disabled:opacity-40"
        >
          {creatingThread ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <MessageSquare size={15} />
          )}
          {threads.length > 0 ? "Open Team Thread" : "Message Team"}
        </button>
      </div>

      {/* Delete team */}
      {isOwner && (
        <button
          onClick={handleDelete}
          className="w-full py-3 border border-red-900/40 rounded-xl text-red-500 text-sm flex items-center justify-center gap-2 hover:bg-red-900/10 transition-colors"
        >
          <Trash2 size={15} />
          Delete Team
        </button>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MY TEAMS VIEW
// ═══════════════════════════════════════════════════════════════════

const MyTeamsView = ({ user, facility, onCountChange }) => {
  const userId = user?.id;
  const fid = facility?.facility_id || "";
  const [myTeams, setMyTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [facilityUsers, setFacilityUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);
  const [responding, setResponding] = useState(null);

  const fetchTeams = useCallback(() => {
    const fidParam = fid ? `?facility_id=${encodeURIComponent(fid)}` : "";
    authFetch(`/fieldiq/my-teams${fidParam}`).then((d) => {
      if (Array.isArray(d)) {
        setMyTeams(d);
        onCountChange?.(d.length);
      }
    });
    authFetch("/fieldiq/my-invites").then((d) => {
      if (Array.isArray(d)) setInvites(d);
    });
  }, [fid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fidParam = fid ? `?facility_id=${encodeURIComponent(fid)}` : "";
    Promise.all([
      authFetch(`/fieldiq/my-teams${fidParam}`),
      authFetch("/fieldiq/my-invites"),
      authFetch(`/fieldiq/team${fidParam}`),
    ])
      .then(([teams, invs, facUsers]) => {
        if (Array.isArray(teams)) {
          setMyTeams(teams);
          onCountChange?.(teams.length);
        }
        if (Array.isArray(invs)) setInvites(invs);
        if (Array.isArray(facUsers)) setFacilityUsers(facUsers);
      })
      .finally(() => setLoading(false));
  }, [fid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (!createName.trim() || creating) return;
    setCreating(true);
    const res = await authFetch("/fieldiq/teams", {
      method: "POST",
      body: JSON.stringify({ name: createName.trim(), facility_id: fid }),
    });
    setCreating(false);
    if (res?.id) {
      setShowCreate(false);
      setCreateName("");
      fetchTeams();
    }
  };

  const handleRespond = async (teamId, accept) => {
    setResponding(teamId);
    await authFetch(`/fieldiq/teams/${teamId}/respond`, {
      method: "POST",
      body: JSON.stringify({ accept }),
    });
    setResponding(null);
    fetchTeams();
  };

  if (selectedTeam) {
    const freshTeam =
      myTeams.find((t) => t.id === selectedTeam.id) || selectedTeam;
    return (
      <TeamDetailView
        team={freshTeam}
        userId={userId}
        facilityUsers={facilityUsers}
        onBack={() => setSelectedTeam(null)}
        onRefresh={fetchTeams}
        user={user}
        facility={facility}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 size={20} className="animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
            Invites
          </p>
          {invites.map((inv) => (
            <div
              key={inv.team_id}
              className="bg-slate-800/40 border border-cyan-700/40 rounded-xl p-3.5 flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">
                  {inv.team_name}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Invited by {inv.invited_by_name || "a team member"}
                </p>
              </div>
              <button
                onClick={() => handleRespond(inv.team_id, true)}
                disabled={responding === inv.team_id}
                className="px-3 py-1.5 bg-cyan-600 text-white text-xs font-semibold rounded-lg hover:bg-cyan-500 disabled:opacity-40"
              >
                {responding === inv.team_id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  "Accept"
                )}
              </button>
              <button
                onClick={() => handleRespond(inv.team_id, false)}
                disabled={responding === inv.team_id}
                className="px-3 py-1.5 bg-slate-700 text-slate-300 text-xs font-semibold rounded-lg hover:bg-slate-600 disabled:opacity-40"
              >
                Decline
              </button>
            </div>
          ))}
        </div>
      )}

      {/* My teams list */}
      {myTeams.length === 0 ? (
        <p className="text-center text-slate-500 text-sm py-6">
          No teams yet — create one below.
        </p>
      ) : (
        <div className="space-y-2.5">
          {myTeams.map((team) => (
            <button
              key={team.id}
              onClick={() => setSelectedTeam(team)}
              className="w-full bg-slate-800/40 rounded-xl p-3.5 border border-slate-700 flex items-center gap-3 text-left hover:border-slate-600 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <Users size={16} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold">{team.name}</span>
                  {team.is_owner && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                      Owner
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  {team.member_count} member
                  {team.member_count !== 1 ? "s" : ""}
                </p>
              </div>
              <ChevronRight
                size={18}
                className="text-slate-600 flex-shrink-0"
              />
            </button>
          ))}
        </div>
      )}

      {/* Create team */}
      {showCreate ? (
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-white">New Team</p>
          <input
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            placeholder="Team name"
            autoFocus
            className="w-full px-3 py-2.5 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowCreate(false);
                setCreateName("");
              }}
              className="flex-1 py-2 border border-slate-600 rounded-lg text-slate-400 text-sm hover:bg-slate-700/40"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!createName.trim() || creating}
              className="flex-1 py-2 bg-cyan-600 rounded-lg text-white text-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-1.5"
            >
              {creating && <Loader2 size={12} className="animate-spin" />}
              Create
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowCreate(true)}
          className="w-full py-3 border border-dashed border-slate-600 rounded-xl text-slate-400 text-sm flex items-center justify-center gap-2 hover:border-slate-500 hover:text-slate-300 transition-colors"
        >
          <Plus size={16} />
          Create Team
        </button>
      )}
    </div>
  );
};

export const PeopleTab = ({ user, facility, isOnline }) => {
  const fid = facility?.facility_id || null;
  const [segment, setSegment] = useState("voters");
  const [voters, setVoters] = useState(loadRoster);
  const [networkContacts, setNetworkContacts] = useState(loadNetwork);
  const [myTeamCount, setMyTeamCount] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedGradient, setSelectedGradient] = useState("");
  const [contactsSupported] = useState(
    () => "contacts" in navigator && "ContactsManager" in window,
  );
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addSubmitting, setAddSubmitting] = useState(false);

  // Fetch voters, network, and team from API on mount
  useEffect(() => {
    authFetch("/fieldiq/contacts").then((data) => {
      if (Array.isArray(data) && data.length > 0) {
        const mapped = data.map((r) => ({
          id: r.id,
          local_id: r.local_id,
          name: r.name,
          phone: r.phone || "—",
          email: r.email || "—",
          status: r.status || "New",
          lastContact: r.updated_at ? timeAgo(r.updated_at) : "Just added",
        }));
        setVoters(mapped);
        saveRoster(mapped);
      }
      // If API returns empty, keep cached data — don't wipe local entries
    });
    authFetch("/fieldiq/network").then((data) => {
      if (Array.isArray(data) && data.length > 0) {
        setNetworkContacts(data);
        saveNetwork(data);
      }
    });
    const fid = facility?.facility_id || "";
    const fidParam = fid ? `?facility_id=${encodeURIComponent(fid)}` : "";
    authFetch(`/fieldiq/voters${fidParam}`).then((data) => {
      if (!Array.isArray(data)) return;
      const enrolled = data.map((v) => ({
        id: `enrolled-${v.id}`,
        local_id: null,
        name: v.name,
        phone: v.phone || "—",
        email: "—",
        status: "Enrolled",
        lastContact: v.created_at ? timeAgo(v.created_at) : "Enrolled",
        enrolled_via: v.enrolled_via || "direct",
        notes: v.notes || "",
      }));
      setVoters((prev) => {
        const existingNames = new Set(prev.map((c) => c.name.toLowerCase()));
        const deduped = enrolled.filter(
          (e) => !existingNames.has(e.name.toLowerCase()),
        );
        return [...prev, ...deduped];
      });
    });
    authFetch(`/fieldiq/my-teams${fidParam}`).then((d) => {
      if (Array.isArray(d)) setMyTeamCount(d.length);
    });
  }, [facility]);

  const openPerson = (person, gradientClass) => {
    setSelectedPerson(person);
    setSelectedGradient(gradientClass);
  };

  const closePerson = () => setSelectedPerson(null);

  if (selectedPerson) {
    return (
      <ContactDetailView
        contact={selectedPerson}
        onBack={closePerson}
        gradientClass={selectedGradient}
      />
    );
  }

  // Save a new voter contact to the DB and update local state
  const persistVoter = async (entry) => {
    const payload = {
      local_id: entry.id,
      name: entry.name,
      phone: entry.phone !== "—" ? entry.phone : null,
      email: entry.email !== "—" ? entry.email : null,
      status: entry.status || "New",
      facility_id: fid,
    };
    const saved = await authFetch("/fieldiq/contacts", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    // Replace temp local_id with server id if returned
    if (Array.isArray(saved) && saved[0]?.id) {
      return { ...entry, id: saved[0].id };
    }
    return entry;
  };

  const pickFromContacts = async () => {
    try {
      const selected = await navigator.contacts.select(
        ["name", "tel", "email"],
        { multiple: true },
      );
      if (!selected.length) return;
      const incoming = selected.map((c) => ({
        id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name: c.name?.[0] || "Unknown",
        phone: c.tel?.[0] || "—",
        email: c.email?.[0] || "—",
        lastContact: "Just added",
        status: "New",
      }));
      // Bulk POST to API
      const payload = incoming.map((e) => ({
        local_id: e.id,
        name: e.name,
        phone: e.phone !== "—" ? e.phone : null,
        email: e.email !== "—" ? e.email : null,
        status: "New",
        facility_id: fid,
      }));
      authFetch("/fieldiq/contacts", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setVoters((prev) => {
        const merged = [...incoming, ...prev];
        saveRoster(merged);
        return merged;
      });
    } catch (err) {
      if (err.name !== "AbortError") {
        alert("Could not access contacts: " + err.message);
      }
    }
  };

  const addManual = () => {
    setAddName("");
    setAddPhone("");
    setAddEmail("");
    setAddOpen(true);
  };

  const handleAddSubmit = async () => {
    const name = addName.trim();
    if (!name || addSubmitting) return;
    setAddSubmitting(true);
    const entry = {
      id: `c-${Date.now()}`,
      name,
      phone: addPhone.trim() || "—",
      email: addEmail.trim() || "—",
      lastContact: "Just added",
      status: "New",
    };
    let saved = entry;
    try {
      saved = await persistVoter(entry);
    } catch (_) {}
    setVoters((prev) => {
      const updated = [saved, ...prev];
      saveRoster(updated);
      return updated;
    });
    setAddSubmitting(false);
    setAddOpen(false);
  };

  // Network import handlers
  const handleImportAllNetwork = async () => {
    if (!("contacts" in navigator && "ContactsManager" in window)) {
      alert("Device contact import is not supported on this browser.");
      return;
    }
    try {
      const selected = await navigator.contacts.select(
        ["name", "tel", "email"],
        { multiple: true },
      );
      if (!selected.length) return;
      const payload = selected.map((c, i) => ({
        local_id: `nc-${Date.now()}-${i}`,
        name: c.name?.[0] || "Unknown",
        phone: c.tel?.[0] || null,
        email: c.email?.[0] || null,
        relation: "Contact",
        tags: [],
        facility_id: fid,
      }));
      const saved = await authFetch("/fieldiq/network", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (Array.isArray(saved)) {
        setNetworkContacts((prev) => {
          const updated = [...saved, ...prev];
          saveNetwork(updated);
          return updated;
        });
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        alert("Could not access contacts: " + err.message);
      }
    }
  };

  const handleImportSingleNetwork = async (contact) => {
    const tempContact = {
      id: `tmp-${Date.now()}`,
      name: contact.name,
      phone: contact.phone || null,
      relation: contact.relation || "Contact",
      tags: [],
      imported_at: new Date().toISOString(),
    };
    setNetworkContacts((prev) => {
      const updated = [tempContact, ...prev];
      saveNetwork(updated);
      return updated;
    });
    try {
      const payload = { ...contact, facility_id: fid };
      const saved = await authFetch("/fieldiq/network", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (Array.isArray(saved) && saved[0]) {
        setNetworkContacts((prev) => {
          const updated = prev.map((c) =>
            c.id === tempContact.id ? saved[0] : c,
          );
          saveNetwork(updated);
          return updated;
        });
      }
    } catch (_) {}
  };

  const handleDeleteNetwork = async (contactId) => {
    await authFetch(`/fieldiq/network/${contactId}`, { method: "DELETE" });
    setNetworkContacts((prev) => {
      const updated = prev.filter((c) => c.id !== contactId);
      saveNetwork(updated);
      return updated;
    });
  };

  const filteredVoters = voters.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const segments = [
    { id: "voters", label: "Contacts", count: voters.length },
    { id: "network", label: "My Network", count: networkContacts.length },
    { id: "team", label: "Team", count: myTeamCount },
  ];

  const subtitle =
    segment === "voters"
      ? `${voters.length} contacts in your roster`
      : segment === "network"
        ? networkContacts.length > 0
          ? `${networkContacts.length} contacts in your network`
          : "Connect your network to see matches"
        : "My teams";

  return (
    <div className="space-y-4">
      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold text-white">People</h1>
        <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
      </div>

      {!isOnline && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-900/40 border border-amber-700/50 rounded-xl text-amber-300 text-xs">
          <WifiOff size={13} />
          Offline — showing cached contacts. New additions will sync when
          reconnected.
        </div>
      )}

      {/* 3-segment control */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-1 flex gap-1">
        {segments.map(({ id, label, count }) => (
          <button
            key={id}
            onClick={() => {
              setSegment(id);
              setSearch("");
            }}
            className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
              segment === id
                ? "bg-gradient-to-r from-cyan-500 to-blue-600 shadow-md shadow-blue-900/30"
                : "bg-transparent"
            }`}
          >
            <span
              className={`text-xs font-semibold ${segment === id ? "text-white" : "text-slate-400"}`}
            >
              {label}
            </span>
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                segment === id
                  ? "bg-white/20 text-white"
                  : "bg-slate-700/60 text-slate-400"
              }`}
            >
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* My Network — full sub-view */}
      {segment === "network" && (
        <MyNetworkView
          contacts={networkContacts}
          onImportAll={handleImportAllNetwork}
          onImportSingle={handleImportSingleNetwork}
          onDelete={handleDeleteNetwork}
        />
      )}

      {/* Action bar — voters only */}
      {segment !== "network" && segment !== "team" && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                segment === "voters" ? "Search contacts..." : "Search team..."
              }
              className="w-full pl-8 pr-3 py-2.5 bg-slate-800/40 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>
          {segment === "voters" ? (
            <>
              {contactsSupported && (
                <button
                  onClick={pickFromContacts}
                  className="px-3 py-2.5 rounded-lg bg-slate-800/40 border border-slate-700 flex items-center"
                  title="Import from device contacts"
                >
                  <Upload size={14} className="text-slate-300" />
                </button>
              )}
              <button
                onClick={addManual}
                className="px-3 py-2.5 rounded-lg bg-cyan-500 flex items-center gap-1.5 shadow-lg shadow-cyan-900/30"
              >
                <Plus size={14} className="text-white" />
                <span className="text-white text-sm font-semibold">Add</span>
              </button>
            </>
          ) : (
            <button className="px-3 py-2.5 rounded-lg bg-slate-800/40 border border-slate-700 flex items-center">
              <Filter size={14} className="text-slate-300" />
            </button>
          )}
        </div>
      )}

      {/* Voters list */}
      {segment === "voters" && (
        <div className="space-y-2.5">
          {filteredVoters.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-10">
              {voters.length === 0
                ? "No voters yet. Pick from your device or tap Add."
                : "No voters match your search."}
            </div>
          ) : (
            filteredVoters.map((c, i) => {
              const tag = VOTER_STATUS_TAGS[c.status] ?? {
                label: c.status,
                color: "slate",
              };
              const grad = PEOPLE_GRADIENTS[i % PEOPLE_GRADIENTS.length];
              return (
                <button
                  key={c.id}
                  onClick={() => openPerson(c, grad)}
                  className="w-full bg-slate-800/40 rounded-xl p-3.5 border border-slate-700 flex items-center gap-3 text-left hover:border-slate-600 transition-colors"
                >
                  <div
                    className={`w-12 h-12 rounded-full bg-gradient-to-br ${grad} flex items-center justify-center flex-shrink-0`}
                  >
                    <span className="text-white font-bold text-sm">
                      {getPersonInitials(c.name)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold">{c.name}</div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-slate-400 text-xs">
                        {c.lastContact}
                      </span>
                      <PeopleTagPill label={tag.label} color={tag.color} />
                    </div>
                    {c.phone && c.phone !== "—" && (
                      <div className="text-slate-500 text-xs mt-1">
                        {c.phone}
                      </div>
                    )}
                  </div>
                  <ChevronRight
                    size={18}
                    className="text-slate-600 flex-shrink-0"
                  />
                </button>
              );
            })
          )}
        </div>
      )}

      {/* Team — user-created small teams */}
      {segment === "team" && (
        <MyTeamsView
          user={user}
          facility={facility}
          onCountChange={setMyTeamCount}
        />
      )}

      {/* Add Contact bottom sheet */}
      {addOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          onClick={() => !addSubmitting && setAddOpen(false)}
        >
          <div
            className="w-full bg-slate-900 border-t border-slate-700 rounded-t-2xl p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-white">Add Contact</span>
              <button
                onClick={() => setAddOpen(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5">
                  Full Name <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder="Jane Smith"
                  autoFocus
                  className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5">
                  Phone
                </label>
                <input
                  type="tel"
                  value={addPhone}
                  onChange={(e) => setAddPhone(e.target.value)}
                  placeholder="(602) 555-0100"
                  className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="jane@example.com"
                  className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>
            </div>

            <button
              onClick={handleAddSubmit}
              disabled={!addName.trim() || addSubmitting}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-700 text-white text-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {addSubmitting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <UserPlus size={16} />
              )}
              {addSubmitting ? "Saving…" : "Add Contact"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DOOR KNOCK SELF-ENROLLMENT FLOW
// Step 0 → canvasser shows QR  Step 1 → voter fills form (simulated)
// Step 2 → canvasser sees confirmation
